import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import {
  getCachedJobPosition,
  getRegistryTenantIdFromSession,
  profileJobPosition,
} from 'shared/utils/sessionProfileStore';
import { softwareApi } from '../../api/systemRegisterApi';
import DeploymentStageApprovalDialog from '../../shared/DeploymentStageApprovalDialog';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
  reportKeepTogetherSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';
import { implementationStatusAfterApprovals } from './changeApprovalState';
import {
  CHANGE_PRIORITY,
  CHANGE_RISK_LEVEL,
  CHANGE_TYPE,
  formatEnumLabel,
} from '../../constants/enums';

const WIZARD_SNAPSHOT_KEY = 'sr-register-wizard-snapshot';
const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'change-management-form';
const PRINTING_BODY_CLASS = 'sr-printing-change-form';

const FIRST_CHANGE_APPROVAL_PERMISSION = 'PERFORM_FIRST_CHANGE_APPROVAL';
const SECOND_CHANGE_APPROVAL_PERMISSION = 'PERFORM_SECOND_CHANGE_APPROVAL';

function isAdminRole(user) {
  const role = user?.data?.role;
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'ADMIN';
}

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return v || v === 0 ? String(v) : '';
}

function formatDisplayDate(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (s.includes('T')) return s.replace('T', ' ').slice(0, 16);
  return s;
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

const EnumCheckRow = ({ label, options, value, form }) => (
  <TableRow>
    <FieldCell label={label} border={form.border} muted={form.muted} colSpan={3}>
      <Box display="flex" flexWrap="wrap" gap={{ xs: 0.5, sm: 2 }}>
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                size="small"
                checked={value === opt}
                readOnly
                disableRipple
                tabIndex={-1}
                sx={{
                  py: 0.25,
                  color: form.checkbox || 'inherit',
                  '&.Mui-checked': { color: form.checkbox || 'primary.main' },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                {formatEnumLabel(opt)}
              </Typography>
            }
            sx={{ mr: 1, ml: 0, color: 'inherit' }}
          />
        ))}
      </Box>
    </FieldCell>
  </TableRow>
);

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

function normalizePersonKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function jobPositionForApproval(approval, users, currentUser) {
  const stored = approval?.jobPosition && String(approval.jobPosition).trim();
  if (stored) return stored;
  const email = normalizePersonKey(approval?.signatureConfirmation);
  const name = normalizePersonKey(approval?.approverName);
  const cached = getCachedJobPosition({
    email: approval?.signatureConfirmation,
    name: approval?.approverName,
  });
  if (cached) return cached;
  const match =
    (email && users.find((u) => normalizePersonKey(u.email) === email))
    || (name && users.find((u) => normalizePersonKey(u.name) === name))
    || null;
  if (match?.jobPosition) return String(match.jobPosition).trim();
  if (
    currentUser
    && (
      (email && email === normalizePersonKey(currentUser.email))
      || (name && name === normalizePersonKey(currentUser.name))
    )
    && currentUser.jobPosition
  ) {
    return String(currentUser.jobPosition).trim();
  }
  return '';
}

function approvalDecisionLabel(approval) {
  if (!approval?.approvalStatus) return null;
  if (approval.approvalStatus === 'APPROVED') return 'APPROVED';
  if (approval.approvalStatus === 'REJECTED') return 'DECLINED';
  return formatEnumLabel(approval.approvalStatus);
}

/** Pull the free-text note from stored approvalComments (`… | Comment: …`). */
function extractApprovalComment(approvalComments) {
  if (!approvalComments?.trim()) return '';
  const match = String(approvalComments).match(/\|\s*Comment:\s*([\s\S]+)$/i);
  if (match) return match[1].trim();
  return String(approvalComments).trim();
}

function getDeclinedComments(approvals) {
  return (approvals ?? [])
    .filter((a) => a?.approvalStatus === 'REJECTED')
    .map((a) => ({
      role: a.approverRole || 'Approver',
      comment: extractApprovalComment(a.approvalComments),
      approverName: a.approverName || '',
    }))
    .filter((row) => row.comment);
}

function patchWizardSnapshotChange(updatedChange) {
  try {
    const raw = sessionStorage.getItem(WIZARD_SNAPSHOT_KEY);
    if (!raw || !updatedChange) return;
    const snap = JSON.parse(raw);
    const entries = snap?.wizardData?.changeManagement?.entries;
    if (!Array.isArray(entries)) return;
    const next = entries.map((e) => {
      const sameKey = updatedChange.key && e.key === updatedChange.key;
      const sameId = updatedChange.id != null && e.id === updatedChange.id;
      return sameKey || sameId ? { ...e, ...updatedChange } : e;
    });
    snap.wizardData.changeManagement.entries = next;
    sessionStorage.setItem(WIZARD_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    // ignore snapshot sync failures
  }
}

function extractChangeFromSoftwareDetail(detail, changeId, changeKey) {
  const list = detail?.changeManagements ?? [];
  const matched =
    list.find((c) => changeId != null && c.id === changeId)
    || list.find((c) => changeKey && String(c.key) === String(changeKey));
  if (!matched) return null;
  return {
    ...matched,
    approvals: (matched.approvals ?? []).map((a) => ({
      id: a.id,
      changeManagementId: a.changeManagementId ?? matched.id,
      approverName: a.approverName,
      approverRole: a.approverRole,
      approverCommittee: a.approverCommittee,
      approvalDate: a.approvalDate,
      approvalStatus: a.approvalStatus,
      approvalComments: a.approvalComments,
      approvalLevel: a.approvalLevel,
      signatureConfirmation: a.signatureConfirmation,
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  };
}

/**
 * Paper-style Change Management form view with 1st/2nd approval actions.
 */
export default function ChangeManagementViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const querySoftwareId = searchParams.get('softwareId');
  const queryChangeId = searchParams.get('id');

  const [softwareName, setSoftwareName] = useState(location.state?.softwareName || '');
  const [softwareId, setSoftwareId] = useState(location.state?.softwareId ?? null);
  const [change, setChange] = useState(() => location.state?.change || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSlot, setDialogSlot] = useState(1);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);

  useEffect(() => {
    if (location.state?.change) {
      setChange(location.state.change);
      setSoftwareName(location.state.softwareName || '');
      setSoftwareId(location.state.softwareId ?? null);
      setLoadError('');
      return;
    }

    if (!querySoftwareId || !queryChangeId) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await softwareApi.get(querySoftwareId);
        const detail = unwrapData(response);
        if (cancelled) return;
        const fromApi = extractChangeFromSoftwareDetail(
          detail,
          Number(queryChangeId),
          null
        );
        if (!fromApi) {
          setChange(null);
          setLoadError('Change record not found for this software.');
          return;
        }
        setChange(fromApi);
        setSoftwareName(detail?.softwareName || '');
        setSoftwareId(detail?.id ?? Number(querySoftwareId));
      } catch (error) {
        if (!cancelled) {
          setChange(null);
          setLoadError(apiErrorMessage(error, 'Failed to load change management details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.state, querySoftwareId, queryChangeId]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      const registryTenantId = getRegistryTenantIdFromSession();
      if (!registryTenantId) return;
      try {
        const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
        if (!cancelled) setTenantUsers(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setTenantUsers([]);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const approvals = change?.approvals ?? [];
  const firstApproval = findSlotApproval(approvals, 1);
  const secondApproval = findSlotApproval(approvals, 2);
  const anyDeclined = [firstApproval, secondApproval].some((a) => a?.approvalStatus === 'REJECTED');
  const declinedComments = anyDeclined ? getDeclinedComments(approvals) : [];

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const isAdmin = isAdminRole(user);
  const canFirstApproval =
    isAdmin || sessionHasPermission(profile, FIRST_CHANGE_APPROVAL_PERMISSION);
  const canSecondApproval =
    isAdmin || sessionHasPermission(profile, SECOND_CHANGE_APPROVAL_PERMISSION);
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantPhoto = tenantBranding?.tenantPhoto;
  const tenantLogoSrc = useProfilePhotoSrc(tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const currentUser = useMemo(
    () => ({
      name: profile?.name?.trim() || profile?.fullName?.trim() || '',
      email: profile?.email?.trim() || '',
      jobPosition:
        profileJobPosition(profile)
        || getCachedJobPosition({
          id: profile?.id,
          email: profile?.email,
          name: profile?.name || profile?.fullName,
        }),
    }),
    [profile]
  );

  const applyLocalApproval = useCallback(
    ({ slot, decision, comment }) => {
      const role = slotRole(slot);
      const declined = decision === 'DECLINED';
      const today = new Date().toISOString().slice(0, 10);
      const displayName = currentUser.name || currentUser.email || 'Approver';
      const email = currentUser.email || '';
      const nextApproval = {
        id: findSlotApproval(change?.approvals, slot)?.id ?? null,
        changeManagementId: change?.id ?? null,
        approverName: displayName,
        approverRole: role,
        approverCommittee: '',
        approvalDate: today,
        approvalStatus: declined ? 'REJECTED' : 'APPROVED',
        approvalComments:
          `${declined ? 'Declined' : 'Approved'} change`
          + (email ? ` | Approver email: ${email}` : '')
          + (comment ? ` | Comment: ${comment}` : ''),
        approvalLevel: slot === 1 ? 'TECHNICAL' : 'BUSINESS',
        signatureConfirmation: email,
        jobPosition: currentUser.jobPosition || '',
      };

      const withoutSlot = (change?.approvals ?? []).filter((a) => a?.approverRole !== role);
      const nextApprovals = [...withoutSlot, nextApproval];
      const nextStatus = implementationStatusAfterApprovals(
        nextApprovals,
        change?.implementationStatus
      );

      const updated = {
        ...change,
        implementationStatus: nextStatus,
        approvals: nextApprovals,
      };
      setChange(updated);
      patchWizardSnapshotChange(updated);
      return updated;
    },
    [change, currentUser.email, currentUser.jobPosition, currentUser.name]
  );

  const openApprovalDialog = (slot) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) {
      toast.error('You do not have permission for this approval.');
      return;
    }
    if (!currentUser.name && !currentUser.email) {
      toast.error('Logged-in user name or email is required to approve or decline.');
      return;
    }
    setDialogSlot(slot);
    setDialogOpen(true);
  };

  const handleApprovalSubmit = async ({ decision, comment }) => {
    setSaving(true);
    try {
      if (softwareId && change?.id) {
        const response = await softwareApi.recordChangeManagementApproval(softwareId, change.id, {
          slot: dialogSlot,
          name: currentUser.name || currentUser.email,
          email: currentUser.email || '',
          jobPosition: currentUser.jobPosition || '',
          approvalDate: new Date().toISOString().slice(0, 10),
          decision,
          comment,
        });
        const detail = unwrapData(response);
        const fromApi = extractChangeFromSoftwareDetail(detail, change.id, change.key);
        if (fromApi) {
          const updated = { ...fromApi, key: change.key };
          setChange(updated);
          patchWizardSnapshotChange(updated);
        } else {
          applyLocalApproval({ slot: dialogSlot, decision, comment });
        }
      } else {
        applyLocalApproval({ slot: dialogSlot, decision, comment });
      }
      toast.success(
        decision === 'DECLINED'
          ? `${slotRole(dialogSlot)} declined.`
          : `${slotRole(dialogSlot)} approved.`
      );
      setDialogOpen(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save change approval.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !change || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const labelBase = (change.title || softwareName || 'change-management')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'change-management';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-CHANGE-MANAGEMENT-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [change, downloadingPdf, softwareName]);

  // Print CSS: only the form, no app chrome / URL / React App title band.
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-change-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          overflow: visible !important;
          border: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  const renderApprovalButton = (slot, approval) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) return null;

    const decision = approvalDecisionLabel(approval);
    const label = slotRole(slot);
    return (
      <Button
        key={slot}
        variant={decision === 'APPROVED' ? 'contained' : 'outlined'}
        color={decision === 'DECLINED' ? 'error' : decision === 'APPROVED' ? 'success' : 'secondary'}
        onClick={() => openApprovalDialog(slot)}
        disabled={saving}
        sx={{ textTransform: 'none' }}
      >
        {decision ? `${label} (${decision})` : label}
      </Button>
    );
  };

  const renderApprovalCell = () => {
    if (!firstApproval && !secondApproval) {
      return (
        <Typography sx={{ fontSize: '0.85rem', color: 'inherit', opacity: 0.7, pl: 2 }}>
          —
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1.5} sx={{ pl: 2, pr: 1.5, py: 1 }}>
        {[firstApproval, secondApproval].filter(Boolean).map((a) => {
          const roleLabel = a.approverRole || 'Approval';
          const signatureEmail = a.signatureConfirmation || '';
          const jobPosition = jobPositionForApproval(a, tenantUsers, currentUser);
          return (
            <Box
              key={a.approverRole || a.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                columnGap: 4,
                rowGap: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'inherit' }}>
                  {roleLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                  {a.approverName || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'inherit' }}>
                  {approvalDecisionLabel(a) || '—'}
                  {a.approvalDate ? ` — ${a.approvalDate}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.75 }}>
                  Signature
                </Typography>
                <Typography
                  sx={{
                    fontFamily:
                      '"Segoe Script", "Lucida Handwriting", "Bradley Hand", "Comic Sans MS", cursive',
                    fontSize: '1.05rem',
                    lineHeight: 1.3,
                    color: 'inherit',
                  }}
                >
                  {jobPosition || '—'}
                </Typography>
                {signatureEmail ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.85 }}>
                    {signatureEmail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!change) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Change Management Form" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError
              || 'Open a change from the Change Management step using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Change Management Form" />
      </Box>

      <Box className="no-print" display="flex" flexDirection="column" gap={1} mb={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {renderApprovalButton(1, firstApproval)}
            {renderApprovalButton(2, secondApproval)}
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Button
              type="button"
              startIcon={<PrintOutlinedIcon />}
              onClick={handlePrint}
              variant="outlined"
            >
              Print
            </Button>
            <Button
              type="button"
              startIcon={
                downloadingPdf ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PictureAsPdfOutlinedIcon />
                )
              }
              onClick={handleDownloadPdf}
              variant="contained"
              color="secondary"
              disabled={downloadingPdf}
            >
              {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          </Box>
        </Box>

        {declinedComments.length > 0 ? (
          <Box
            sx={{
              alignSelf: 'stretch',
              maxWidth: 980,
              mx: 'auto',
              width: '100%',
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: (t) => alpha(t.palette.error.main, isDark ? 0.16 : 0.06),
              borderRadius: 1,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="subtitle2" color="error.main" fontWeight={800} mb={0.5}>
              Approval comments
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {declinedComments.map((item) => (
                <Box key={item.role}>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    {item.role}
                    {item.approverName ? ` · ${item.approverName}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {item.comment}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}` : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Change Management Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR CM1
            </Typography>
          </Box>
        </Box>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Software / System Name:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(softwareName) || '—'}
              </FieldCell>
              <FieldCell label="Requested By:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(change.requestedBy) || '—'}
              </FieldCell>
              <FieldCell label="Implementation Status:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(change.implementationStatus) || '—'}
              </FieldCell>
            </TableRow>

            <EnumCheckRow
              label="Change Type (Please Select):"
              options={CHANGE_TYPE}
              value={change.changeType}
              form={form}
            />
            <EnumCheckRow
              label="Priority (Please Select):"
              options={CHANGE_PRIORITY}
              value={change.priority}
              form={form}
            />
            <EnumCheckRow
              label="Risk Level (Please Select):"
              options={CHANGE_RISK_LEVEL}
              value={change.riskLevel}
              form={form}
            />

            <TableRow>
              <FieldCell label="Change Title:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(change.title) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Description:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.description) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Reason for Change:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.reasonForChange) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Current Version:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(change.currentVersion) || '—'}
              </FieldCell>
              <FieldCell label="Target Version:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(change.targetVersion) || '—'}
              </FieldCell>
              <FieldCell label="Requested Date:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatDisplayDate(change.requestedDate) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Scheduled Start:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {formatDisplayDate(change.scheduledStart) || '—'}
              </FieldCell>
              <FieldCell label="Scheduled End:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatDisplayDate(change.scheduledEnd) || '—'}
              </FieldCell>
              <FieldCell label="Downtime Required:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {change.downtimeRequired
                  ? `Yes${change.downtimeDuration ? ` (${dash(change.downtimeDuration)} min)` : ''}`
                  : 'No'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Implementation Plan:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.implementationPlan) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Rollback Plan:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.rollbackPlan) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Test Plan:" border={form.border} muted={form.muted} sx={{ width: '66%' }} colSpan={2}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.testPlan) || '—'}</Box>
              </FieldCell>
              <FieldCell label="Test Result:" border={form.border} muted={form.muted}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(change.testResult) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Actual Start:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {formatDisplayDate(change.actualStart) || '—'}
              </FieldCell>
              <FieldCell label="Actual End:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatDisplayDate(change.actualEnd) || '—'}
              </FieldCell>
              <FieldCell label="Implemented By:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(change.implementedBy) || '—'}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box data-pdf-keep="true" sx={reportKeepTogetherSx}>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  bgcolor: form.headBg,
                  color: 'inherit',
                  py: 1,
                  '@media print': {
                    bgcolor: '#f5f5f5 !important',
                    color: '#111 !important',
                    border: '1px solid #1a1a1a !important',
                  },
                }}
              >
                Approval
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  minHeight: 88,
                  py: 1.25,
                  color: 'inherit',
                }}
              >
                {renderApprovalCell()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this form for audit and compliance.
        </Box>
        </Box>
      </Paper>

      <DeploymentStageApprovalDialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        onSubmit={handleApprovalSubmit}
        stageStatus="CHANGE"
        slot={dialogSlot}
        currentUser={currentUser}
        saving={saving}
        existingComment=""
        title={`${slotRole(dialogSlot)} — Change Management`}
        description="Approve this change, or decline it and leave a comment."
      />
    </Box>
  );
}
